import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaExternalLinkAlt, FaInfoCircle, FaImage, FaClipboardCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import AnimatedModal from './AnimatedModal';
import api from '../../../api';

interface FinalizarIntegracaoFotosModalProps {
  banca: any;
  processo: any;
  onClose: () => void;
  onConfirm: () => void;
}

interface EstatisticasCandidatos {
  total: number;
  comResultado: number;
  semResultado: number;
  comFoto: number;
  semFoto: number;
  totalProcesso: number;
  comFotoProcesso: number;
  semFotoProcesso: number;
}

const FinalizarIntegracaoFotosModal: React.FC<FinalizarIntegracaoFotosModalProps> = ({
  banca,
  processo,
  onClose,
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<EstatisticasCandidatos | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get(`/bancas/${banca.id}/estatisticas-candidatos`);
        setStats(response.data);
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
        toast.error('Erro ao carregar estatísticas dos candidatos');
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [banca.id]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Erro ao confirmar integração:', error);
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (com: number, total: number) => {
    if (total === 0) return 'Nenhum candidato';
    if (com === total) return 'Todos';
    if (com === 0) return 'Nenhum';
    return 'Alguns';
  };

  const getRevisaoUrl = () => {
    return `/processos/${processo?.id}/bancas/${banca.id}/candidatos`;
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white', padding: '32px', borderRadius: '12px',
        width: '600px', maxWidth: '90vw', maxHeight: '80vh',
        overflow: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: '#dbeafe', marginBottom: '12px'
          }}>
            <FaImage style={{ color: '#2563eb', fontSize: '20px' }} />
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            Concluir Integração de Fotos
          </h3>
        </div>

        <div style={{
          backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
          marginBottom: '20px', border: '1px solid #0ea5e9'
        }}>
          <div style={{ fontSize: '14px', color: '#0c4a6e', lineHeight: '1.5' }}>
            Certifique-se de que todas as fotos enviadas por Integração (COMVEST ou outras Unidades)
            estejam corretas e em acordo com o Processo.
          </div>
        </div>

        {processo && (
          <div style={{
            backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', border: '1px solid #e9ecef'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Processo:</strong> {processo.nome}
            </div>
            <a
              href={getRevisaoUrl()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#2563eb', textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              <FaExternalLinkAlt style={{ fontSize: '12px' }} />
              Clique aqui para revisar a lista de candidatos enviados por Integração antes de prosseguir
            </a>
          </div>
        )}

        {loadingStats ? (
          <div style={{
            backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', textAlign: 'center', color: '#6b7280'
          }}>
            Carregando estatísticas...
          </div>
        ) : stats && (
          <div style={{
            backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
            marginBottom: '20px', border: '1px solid #0ea5e9'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: '600', color: '#0c4a6e' }}>
              Estatísticas de Fotos:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div style={{
                backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                  <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Fotos da Banca</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div><strong>{getStatusText(stats.comFoto, stats.total)}</strong> têm foto</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                    {stats.comFoto} de {stats.total} candidatos
                  </div>
                </div>
              </div>
              <div style={{
                backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                  <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Fotos do Processo</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div><strong>{getStatusText(stats.comFotoProcesso, stats.totalProcesso)}</strong> têm foto</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                    {stats.comFotoProcesso} de {stats.totalProcesso} candidatos do processo
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div style={{
            backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
            marginBottom: '12px', border: '1px solid #0ea5e9',
            display: 'flex', alignItems: 'flex-start', gap: '12px'
          }}>
            <FaInfoCircle style={{ color: '#0ea5e9', fontSize: '16px', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
              <strong>Em caso de fotos incorretas:</strong> Informar o Integrador (COMVEST ou Unidade que fez a integração)
              sobre as fotos incorretas e <strong>aguardar</strong> a atualização que as corrija.
            </div>
          </div>

          <div style={{
            backgroundColor: '#fee2e2', padding: '16px', borderRadius: '8px',
            marginBottom: '12px', border: '1px solid #fca5a5',
            display: 'flex', alignItems: 'flex-start', gap: '12px'
          }}>
            <FaExclamationTriangle style={{ color: '#dc2626', fontSize: '16px', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#991b1b' }}>
              <strong>NÃO confirme esta finalização se houverem fotos incorretas, não corrigidas.</strong>
            </div>
          </div>

          <div style={{
            backgroundColor: '#fef3c7', padding: '16px', borderRadius: '8px',
            border: '1px solid #fbbf24',
            display: 'flex', alignItems: 'flex-start', gap: '12px'
          }}>
            <FaExclamationTriangle style={{ color: '#d97706', fontSize: '16px', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              <strong>Atenção:</strong> Após a confirmação, nenhuma alteração poderá ser feita nas Fotos
              dos candidatos desta banca, nem pelo Integrador (COMVEST ou outras Unidades) nem pela DEDH.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', border: '1px solid #d1d5db',
              backgroundColor: 'white', borderRadius: '6px',
              cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: '10px 20px', backgroundColor: '#2563eb',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500',
              opacity: loading ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            {loading ? 'Confirmando...' : (
              <>
                <FaImage style={{ fontSize: '12px' }} />
                Finalizar Integração de Fotos
              </>
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default FinalizarIntegracaoFotosModal;
